import {
  ActionList, ActionListItem, AlertVariant, Button, Card,
  Flex, FlexItem, Form, FormGroup, Icon,
  PageSection, Stack,
  TextArea, TextInput, Title
} from '@patternfly/react-core';
import { PlusCircleIcon, TrashIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { TypeaheadSelect } from '@patternfly/react-templates';
import * as React from 'react';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { PhoneInput } from '@app/components/PhoneInput';
import { isNotFound } from '@app/utils/ApiError';
import { useNavigate, useParams } from 'react-router-dom';

import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { FormAddress } from '@app/components/FormAddress';
import { useEntity } from '@app/hooks/useEntity';
import { Partner, PartnerContact } from '@app/model/Partner';
import { useClientExecutiveStore, usePartnerStore } from '@app/store/entityStore';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';

const emptyPartner : Partial<Partner> = {
  contacts: []
};

const PartnerDetail: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  
  const [partner, setPartner] = React.useState<Partial<Partner>>(emptyPartner);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  const loadPartner = usePartnerStore(state => state.loadById);
  const savePartner = usePartnerStore(state => state.save);
  const removePartner = usePartnerStore(state => state.delete);

  const {items: clientExecutives} = useEntity(useClientExecutiveStore);

  const [isDirty, setIsDirty] = React.useState(false);
  /** No partner answers to that id, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState(false);

  useBreadcrumbLabel( id === 'new' ? 'New' : partner.name);

  React.useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setNotFound(false);
      beginLoading();
      try {
        if (id !== 'new'){
          const p = await loadPartner(id as string);

          const cloned = structuredClone(p);
          setPartner(cloned);
        }
      } catch (error: any) {
        // An id matching no partner is the answer rather than a failure: the page renders it.
        if (!isMounted) return;
        if (isNotFound(error)) setNotFound(true);
        else addNotification('Error loading data', AlertVariant.danger, error.message);
      } finally {
        endLoading();
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [id]);

  const updateField = (field: keyof Partner, value: any) => {
    setPartner(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleContactChange = (index: number, field: keyof PartnerContact, value: string) => {
    const updatedContacts = [...partner.contacts || []];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value } as PartnerContact;
    updateField('contacts', updatedContacts);
  };

  const addContact = () => {
    const newContact: PartnerContact = { name: '', email: '', mobile: '', role: '' } as PartnerContact;
    updateField('contacts', [...(partner.contacts || []), newContact]);
  };

  const removeContact = (index: number) => {
    const updatedContacts = partner.contacts?.filter((_, i) => i !== index);
    updateField('contacts', updatedContacts);
  };

  const onSaveClick = async () => {
    if (!partner.name?.trim()) {
      addNotification('Partner name is required.', AlertVariant.danger);
      return;
    }
    
    beginLoading();
    try {
      const isNew = id === 'new';
      
      const savedData = await savePartner(partner, isNew);
      setPartner(savedData);
      
      addNotification('Partner saved successfully!', AlertVariant.success);
      setIsDirty(false);
      
      if (id === 'new') navigate(`/partners/${savedData.id}`, { replace: true });
    } catch (error: any) {
      addNotification('Error saving partner', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const onRefresh = async () => {
    if (id === 'new') return;
    const fresh = await loadPartner(id as string, true);
    setPartner(structuredClone(fresh));
    setIsDirty(false);
  };

  const onRemove = async () => {
    beginLoading();
    try {
      await removePartner(partner.id as number);
      addNotification('Partner removed successfully', AlertVariant.success);
      setIsDirty(false);
      // Same state the layout's Back button carries, so the list comes back filtered as it was
      navigate('/partners', { state: { restoreFilters: true } });
    } catch (error: any) {
      addNotification('Error removing partner', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const onCancel = async () => {
    if (id === 'new') {
      setPartner(emptyPartner);
    } else {
      const p = await loadPartner(id as string);
      setPartner(p);
    }
    setIsDirty(false);
  };

  if (notFound) {
    return (
      <NotFoundState
        entityLabel="partner"
        identifier={id}
        backTo={{ path: '/partners', label: 'Back to partners' }}
      />
    );
  }

  if (!partner && id !== 'new') return null;

  const changeLogTitle = partner.name;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = partner.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="partner"
      id={partner.id}
      entityLabel="this partner"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={id === 'new' ? 'New Partner' : partner.name || 'Editing Partner'}
        actions={
          <ActionList>
            <ActionListItem>
              <Button variant="primary" isDisabled={!isDirty} onClick={onSaveClick}>Save</Button>
            </ActionListItem>
            <ActionListItem>
              <Button variant="link" isDisabled={!isDirty} onClick={onCancel}>Cancel</Button>
            </ActionListItem>
            <ActionListItem>
              <PageActionsMenu
                items={changeLogItems}
                onRefresh={onRefresh}
                onRemove={onRemove}
                isRemoveDisabled={id === 'new'}
                removeConfirmationMessage={`Confirm removal of "${partner.name}"?`}
              />
            </ActionListItem>
          </ActionList>
        }
      />

      <PageSection isWidthLimited>
        <Stack hasGutter>
          <Card style={{ padding: '24px' }}>
            <Title headingLevel="h2" size="xl" style={{ marginBottom: '24px' }}>General Information</Title>
            <Form isHorizontal>
              <FormGroup label="Name" isRequired fieldId="name">
                <TextInput isRequired type="text" id="name" value={partner.name || ''} onChange={(_e, v) => updateField('name', v)} />
              </FormGroup>
              
              <FormGroup label="Client Executive" fieldId="clientExecutive">
                <TypeaheadSelect
                  initialOptions={clientExecutives.map(i => ({ value: i.id as number, content: i.name as string, selected: i.id === partner.clientExecutive?.id }))}
                  placeholder="Select a client executive"
                  selected={partner.clientExecutive?.id}
                  onClearSelection={() => updateField('clientExecutive', null)}
                  onSelect={(_ev, execId) => updateField('clientExecutive', clientExecutives.find(i => i.id === execId))}
                />
              </FormGroup>

              <FormGroup label="Address" fieldId="address">
                <FormAddress value={partner.address} id="address" name="address" onChange={(_e, addr) => updateField('address', addr)} />
              </FormGroup>

              <FormGroup label="Comments" fieldId="comments">
                <TextArea type="text" id="comments" value={partner.comments || ''} onChange={(_e, v) => updateField('comments', v)} />
              </FormGroup>
            </Form>
          </Card>

          <Card style={{ padding: '24px' }}>
            <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }} style={{ marginBottom: '16px' }}>
              <FlexItem><Title headingLevel="h2" size="xl">Contacts</Title></FlexItem>
              {/* FIXED: Replaced isSmall with size="sm" */}
              <FlexItem><Button variant="secondary" icon={<PlusCircleIcon />} onClick={addContact} size="sm">Add Contact</Button></FlexItem>
            </Flex>
            
            <Table aria-label="Contacts Table" variant="compact">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Role</Th>
                  <Th>Email</Th>
                  <Th>Mobile</Th>
                  <Th screenReaderText='Actions'></Th>
                </Tr>
              </Thead>
              <Tbody>
                {partner.contacts && partner.contacts.length > 0 ? (
                  partner.contacts.map((contact, index) => (
                    <Tr key={index}>
                      <Td>
                        <TextInput type="text" value={contact.name || ''} 
                          onChange={(_e, v) => handleContactChange(index, 'name', v)} 
                          placeholder="Contact Name" aria-label={`Contact name ${index + 1}`} />
                      </Td>
                      <Td>
                        <TextInput type="text" value={contact.role || ''} 
                          onChange={(_e, v) => handleContactChange(index, 'role', v)} 
                          placeholder="e.g. Manager" aria-label={`Contact role ${index + 1}`} />
                      </Td>
                      <Td>
                        <TextInput type="email" value={contact.email || ''} 
                          onChange={(_e, v) => handleContactChange(index, 'email', v)} 
                          placeholder="email@domain.com" aria-label={`Contact email ${index + 1}`} />
                      </Td>
                      <Td>
                        <PhoneInput value={contact.mobile || '+44'}
                          onChange={(val) => handleContactChange(index, 'mobile', val)}
                          ariaLabel={`Contact mobile ${index + 1}`} />
                      </Td>
                      <Td modifier="fitContent">
                        <Button 
                          variant="plain" 
                          icon={<Icon status="danger"><TrashIcon /></Icon>} 
                          onClick={() => removeContact(index)} 
                          aria-label="Remove contact" 
                        />
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={5} className="pf-v5-u-text-align-center">No contacts added yet.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Card>
        </Stack>
      </PageSection>
    </EntityChangeLog>
  );
};

export { PartnerDetail };
